import axios from 'axios';
import { api, baseURL } from './index.js';
import { setRefreshToken, setToken } from '@contexts/Token.context.jsx';

export const registerUser = async fields => {
	const url = baseURL + '/auth/register';

	const { name, email, password, passwordConfirm: password_confirm, tier } = fields;

	const user = {
		name,
		username: email,
		email,
		password,
		password_confirm,
		tier,
	};

	const { data } = await axios.post(url, user);
	return data;
};

/**
 * Turns a FastAPI `detail` into a plain array of strings.
 *
 * `detail` is a string for handled errors, but an array of `{ loc, msg, type }`
 * for 422 validation failures. Rendering the latter straight into JSX throws
 * "Objects are not valid as a React child", so the shape is flattened here
 * rather than at each call site.
 */
const detailToMessages = detail => {
	if (typeof detail === 'string') return [detail];

	if (Array.isArray(detail)) {
		return detail
			.map(entry => (typeof entry === 'string' ? entry : entry?.msg))
			.filter(entry => typeof entry === 'string' && entry.length > 0);
	}

	// Shape used by endpoints that return `{ code, message }`.
	if (typeof detail?.message === 'string') return [detail.message];

	return [];
};

/**
 * Normalizes an axios error from the password-reset endpoints so callers can
 * branch on it without inspecting the response themselves.
 *
 * There is no `detail` at all when the request never reached the API (network
 * or CORS failure) or when an error came back from the CDN/load balancer as
 * HTML. Both used to surface as a bare "Unknown error", so they are reported
 * distinctly via `isNetworkError` and an empty `messages`.
 */
export const normalizePasswordResetError = error => {
	const response = error?.response;
	const detail = response?.data?.detail;

	const normalized = new Error(error?.message ?? 'The password reset request failed', {
		cause: error,
	});

	normalized.name = 'PasswordResetError';
	normalized.status = response?.status ?? null;
	normalized.code = typeof detail?.code === 'string' ? detail.code : null;
	normalized.messages = detailToMessages(detail);
	normalized.isNetworkError = !response;

	return normalized;
};

export const requestPasswordReset = async email => {
	const url = baseURL + '/auth/request_reset_password';

	const passwordResetRequest = {
		email,
	};

	try {
		const { data } = await axios.post(url, passwordResetRequest);
		return data;
	} catch (error) {
		throw normalizePasswordResetError(error);
	}
};

export const resetPassword = async fields => {
	const url = baseURL + '/auth/reset_password';

	const { email, token, password, passwordConfirm: password_confirm } = fields;
	const passwordResetData = {
		token,
		email,
		password,
		password_confirm,
	};

	try {
		const { data } = await axios.post(url, passwordResetData);
		return data;
	} catch (error) {
		throw normalizePasswordResetError(error);
	}
};

export const verifyUserEmail = async (email, token) => {
	const url = `${baseURL}/auth/verifyemail?token=${token}&email=${email}`;

	const { data } = await axios.get(url);

	return data;
};

export const loginUser = async fields => {
	const url = baseURL + '/auth/login';

	const { email, password } = fields;
	const user = {
		email,
		password,
	};

	const { data } = await axios.post(url, user, {
		withCredentials: false,
		headers: {
			'Content-Type': 'application/json',
			'Access-Control-Allow-Origin': '*',
			'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
		},
	});

	if (data.access_token) setToken(data.access_token);
	if (data.refresh_token) setRefreshToken(data.refresh_token);

	return data;
};

export const setActiveOrg = async (org_id = '') => {
	const url = baseURL + '/auth/users/set_active_org';

	const { data } = await api.put(url, null, { params: { org_id } });
	return data;
};
